'use client';

import { useState } from 'react';
import { Image } from '@/app/_components/common/Image';
import { Icon } from '@/app_components/common/Icon';
import { ButtonV2 } from '../ui/ButtonV2';

interface ImageProps {
  image?: string;
  imageAlt?: string;
  imageCaption?: string;
}

interface ImageCarouselProps {
  imageList: ImageProps[];
}

export const ImageCarousel = ({ imageList }: ImageCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const prevImage = () => {
    setCurrentIndex((prev) => (prev === 0 ? imageList.length - 1 : prev - 1));
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev === imageList.length - 1 ? 0 : prev + 1));
  };

  const noCarousel = imageList.length < 2;

  return noCarousel ? (
    imageList.length ? (
      <div
        className='flex flex-col gap-1'
        data-cy='ez-newswire-content-page-image'
      >
        <div className='aspect-[3/2] relative bg-helper'>
          <Image
            alt={imageList[0].imageAlt || ''}
            className='object-contain object-center'
            fill
            fullWidth={false}
            src={imageList[0].image || ''}
          />
        </div>
        <div className='font-graphik font-normal typography-xs text-secondary uppercase'>
          {imageList[0].imageCaption}
        </div>
      </div>
    ) : null
  ) : (
    <div
      className='flex flex-col items-end gap-1'
      data-cy='ez-newswire-content-page-image-carousel'
    >
      <div className='overflow-hidden w-full h-full relative aspect-[3/2]'>
        <div
          className='flex transition-transform duration-500 ease-in-out h-full'
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {imageList.map((image, id) => (
            <div
              className='w-full flex-shrink-0 relative bg-helper'
              key={`${image.image}-${id}`}
            >
              <Image
                alt={image.imageAlt || ''}
                className='object-contain object-center'
                fill
                fullWidth={false}
                src={image.image || ''}
              />
            </div>
          ))}
        </div>
      </div>

      <div className='self-stretch font-graphik font-normal typography-xs text-secondary uppercase'>
        {imageList[currentIndex].imageCaption}
      </div>
      <div className='flex gap-4'>
        <ButtonV2
          as='button'
          className='flex w-10 py-[7px] justify-center items-center rounded-full border border-[#D1D1D6] bg-[#f2f2f2] text-primary-hover'
          data-cy='ez-newswire-content-page-image-carousel-button-left'
          isTertiary
          onClick={prevImage}
        >
          <Icon icon='ChevronLeft' />
        </ButtonV2>
        <ButtonV2
          as='button'
          className='flex w-10 py-[7px] justify-center items-center rounded-full border border-[#D1D1D6] bg-[#f2f2f2] text-primary-hover'
          data-cy='ez-newswire-content-page-image-carousel-button-right'
          isTertiary
          onClick={nextImage}
        >
          <Icon icon='ChevronRight' />
        </ButtonV2>
      </div>
    </div>
  );
};
